// src/components/UserProfile.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header'; // Import Header
import './UserProfile.css';
import { 
  Avatar,
  Card, 
  CardContent, 
  Typography, 
  Box, 
  List, 
  ListItem, 
  ListItemText, 
  CircularProgress, 
  Alert,
  Paper,
  Chip
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { useUser } from '../contexts/UserContext'; // Import useUser hook
import { useTheme } from '@mui/material/styles';

const UserProfile = () => {
  const { t } = useTranslation();
  const { userData, userProfile, loadingUser, errorUser } = useUser(); // Use UserContext
  const theme = useTheme();

  const tenantsAllowed = localStorage.getItem('tenants_allowed') ? JSON.parse(localStorage.getItem('tenants_allowed')) : [];

  const renderProfileDetailItem = (icon, label, value) => (
    <ListItem>
      <Box display="flex" alignItems="center" width="100%">
        <Box mr={2} display="flex" alignItems="center">{icon}</Box>
        <ListItemText primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} primary={label} />
        <Typography variant="body1" sx={{ marginLeft: 'auto', textAlign: 'right' }}>{value}</Typography>
      </Box>
    </ListItem>
  );

  return (
    <div className="page-container" style={{ paddingBottom: '80px' }}>
      {/* <Header title={t('userProfile.title')} /> */}
      <Box className="profile-content" sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        {loadingUser && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}
        {errorUser && (
          <Alert severity="error" sx={{ width: '100%', maxWidth: '600px' }}>
            {errorUser.response?.data?.detail || errorUser.message || t('userProfile.errorLoadingContext')}
          </Alert>
        )}
        {!loadingUser && !errorUser && !userData && (
          <Alert severity="info" sx={{ width: '100%', maxWidth: '600px' }}>{t('userProfile.noData')}</Alert>
        )}
        
        {!loadingUser && !errorUser && userData && (
          <Paper elevation={3} sx={{ width: '100%', maxWidth: '700px', borderRadius: '12px' }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                {
                  userProfile.photo ? (
                    <Avatar sx={{ width: 80, height: 80 }} alt={userProfile.full_name} src={userProfile.photo} />
                  ) : (
                    <AccountCircleIcon sx={{ fontSize: 80, color: 'primary.main' }} />
                  )
                }
                <Typography 
                  variant="h4"
                  gutterBottom 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'medium',
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }, // Responsive font size
                    wordBreak: 'break-word', // Handle long emails
                    overflow: 'hidden',
                    maxWidth: '100%',
                    textAlign: 'center'
                  }}
                >
                  {userProfile.first_name + ' ' + userProfile.last_name}
                </Typography>
                <Typography 
                  variant="body1"  
                  gutterBottom 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'medium',
                    wordBreak: 'break-word', // Handle long emails
                    overflow: 'hidden',
                    maxWidth: '100%',
                    textAlign: 'center'
                  }}
                >
                  {userProfile.username || userProfile.email}
                </Typography>
                <Box>
                  {tenantsAllowed.map(tenant => (
                    <Chip sx={{ m: 0.5 }} key={tenant.id} label={tenant.name} icon={<BusinessIcon />} color="secondary" />
                  ))}
                </Box>
              </Box>
              
              <List dense>
                {renderProfileDetailItem(<LanguageIcon color="action"/>, t('userProfile.language'), userProfile.language)}
                {renderProfileDetailItem(<AccessTimeIcon color="action"/>, t('userProfile.timezone'), userProfile.timezone)}
              </List>

              
              {/* Ajout du bouton retour dashboard */}
              <Box mt={4} display="flex" justifyContent="center">
                <a href="/" style={{ textDecoration: 'none' }}>
                  <button style={{
                    padding: '10px 24px',
                    fontSize: '1rem',
                    borderRadius: '6px',
                    background: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}>
                    {t('userProfile.backToDashboard', 'Retour aux événements')}
                  </button>
                </a>
              </Box>
            </CardContent>
          </Paper>
        )}
      </Box>
    </div>
  );
};

export default UserProfile;
